/**
 * @fileoverview Subscription plans comparison table component.
 * @summary Displays a comparison table of all subscription plan features.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/ComparisonTable
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import type { FC } from 'react'

/**
 * Props for the ComparisonTable component.
 */
interface ComparisonTableProps {
  plans: Array<{
    type: SubscriptionType
    titleKey: string
  }>
  t: (key: string, options?: Record<string, any>) => string
}

/**
 * @summary Displays a features comparison table for all plans.
 * @param {ComparisonTableProps} props - Component props.
 * @returns {ReactElement} Comparison table component.
 */
const ComparisonTable: FC<ComparisonTableProps> = ({ plans, t }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="px-6 py-4 bg-gray-900 text-white">
        <h3 className="text-xl font-bold">{t('subscription.features')}</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100 border-b border-gray-300">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
              {plans.map((plan) => (
                <th key={plan.type} className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  {t(plan.titleKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { label: t('subscription.maxEvents'), key: 'maxEvents' },
              { label: t('subscription.maxSongsPerEvent'), key: 'maxSongsPerEvent' },
            ].map((feature) => (
              <tr key={feature.key} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{feature.label}</td>
                {plans.map((plan) => {
                  const limits = SUBSCRIPTION_PLANS[plan.type]
                  const value = feature.key === 'maxEvents' ? limits.maxEvents : limits.maxSongsPerEvent
                  return (
                    <td key={plan.type} className="px-6 py-4 text-center text-sm text-gray-600">
                      {value === Infinity ? (
                        <FontAwesomeIcon icon={faCheck} className="text-green-600 text-lg" />
                      ) : (
                        <span className="font-semibold">{value}</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ComparisonTable

